import type React from "react";
import { memo, useEffect, useState } from "react";

import { faNotebook } from "@fortawesome/pro-regular-svg-icons";
import { Box, Stack, Typography } from "@mui/material";
import { GridActionsCellItem, type GridColDef, type GridSortModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useGetTreatmentActivityLogs } from "api/hooks/useGetTreatmentActivityLogs";
import { type PaginationModel, usePagination } from "api/hooks/usePagination";
import { usePutTreatmentActivityLog } from "api/hooks/usePutTreatmentActivityLog";
import type { LogEntryWithPatientName } from "api/schemas/TreatmentActivityLog";
import {
  ActivityLogConfirmDialog,
  type Dialog,
  initialDialogState,
} from "routes/patients/PatientProfile/components/ActivityLogConfirmDialog";
import { Tooltip } from "shared/atoms/Tooltip";
import { CircleCheckIcon, CircleXmarkIcon, UserMagnifyingGlassIcon } from "shared/icons";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { DARK_BLUE } from "theme/colors";
import { useLocalizedDate } from "utils/date";
import { reportError } from "utils/errorReporting";

const initialPaginationModel: PaginationModel = { page: 0, pageSize: 10 };
const initialSortModel: GridSortModel = [{ field: "patient_name", sort: "asc" }];

export const ActivityLogsTable: React.FC = memo(() => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { pagination, setPagination } = usePagination(initialPaginationModel);
  const [sort, setSort] = useState<GridSortModel>(initialSortModel);
  const { data, isLoading, isFetching, refetch } = useGetTreatmentActivityLogs({
    pagination: { limit: pagination.limit, page: pagination.page + 1 },
    sort: { sort_by: sort[0]?.field, sort_order: sort[0]?.sort },
  });
  const { parseISO, format, isValid } = useLocalizedDate();
  const { mutateAsync, isLoading: isUpdating } = usePutTreatmentActivityLog();
  const rows = data?.items || [];
  const metadata = data?.metadata;
  const [rowCountState, setRowCountState] = useState(metadata?.total_count || 0);
  const [showDialog, setShowDialog] = useState<Dialog>(initialDialogState);
  const [error, setError] = useState<string | null>(null);

  const selectedLog = showDialog.id ? rows.find(row => row.id === showDialog.id) : null;

  useEffect(() => {
    setRowCountState(prevRowCountState =>
      metadata?.total_count !== undefined ? metadata.total_count : prevRowCountState
    );
  }, [metadata, setRowCountState]);

  const updateActivityLog = async () => {
    try {
      if (showDialog.id && showDialog.status) {
        await mutateAsync(
          { id: showDialog.id, data: { status: showDialog.status } },
          {
            onSuccess: () => {
              setShowDialog(initialDialogState);
              refetch();
            },
            onError: () => {
              setError(t("errors.generic"));
            },
          }
        );
      }
    } catch (e) {
      setError(t("errors.generic"));
      if (e instanceof Error || typeof e === "string") {
        reportError("ActivityLogsTable.tsx", e);
      }
    }
  };

  const columns: GridColDef<LogEntryWithPatientName>[] = [
    {
      field: "patient_name",
      headerName: t("dashboard.tables.notes.labels.patient"),
      valueGetter: (_value, row) => `${row.patient_first_name || ""} ${row.patient_last_name || ""}`,
      flex: 1,
    },
    {
      field: "category",
      headerName: t("patients.activity_log.table.activity"),
      valueGetter: (_value, row) => row.category.description || "",
      flex: 1.5,
      sortable: false,
    },
    {
      field: "time",
      headerName: t("patients.activity_log.table.time"),
      valueGetter: (_value, row) => t("patients.activity_log.table.duration", { duration: row.duration / 60 }),
      flex: 1,
      sortable: false,
    },
    {
      field: "start_time",
      headerName: t("patients.activity_log.table.date"),
      valueFormatter: (value: string) => {
        const date = value ? parseISO(value) : new Date();
        return isValid(date) ? format(date, "P") : "";
      },
      flex: 1,
      maxWidth: 120,
    },
    {
      field: "actions",
      type: "actions",
      width: 120,
      getActions: params => [
        <Tooltip title={t("patients.activity_log.table.accept")}>
          <GridActionsCellItem
            icon={<CircleCheckIcon />}
            label={t("patients.activity_log.table.accept")}
            showInMenu={false}
            onClick={() => {
              setShowDialog({
                show: true,
                status: "accepted",
                id: params.row.id,
              });
            }}
            disabled={params.row.status === "accepted"}
          />
        </Tooltip>,
        <Tooltip title={t("patients.activity_log.table.discard")}>
          <GridActionsCellItem
            icon={<CircleXmarkIcon />}
            label={t("patients.activity_log.table.discard")}
            showInMenu={false}
            onClick={() => {
              setShowDialog({
                show: true,
                status: "discarded",
                id: params.row.id,
              });
            }}
            disabled={params.row.status === "discarded"}
          />
        </Tooltip>,
        <Tooltip title={t("booking.buttons.view_profile")}>
          <GridActionsCellItem
            icon={<UserMagnifyingGlassIcon />}
            label={t("booking.buttons.view_profile")}
            showInMenu={false}
            onClick={() => {
              navigate(`/patients/${params.row.patient_id}`);
            }}
          />
        </Tooltip>,
      ],
    },
  ];

  const renderRow = (label: string, value: string) => (
    <Box display="flex">
      <Typography variant="body2" component="span" fontWeight="bold" minWidth={80} textAlign="left" mr={1}>
        {label}:
      </Typography>
      <Typography variant="body2" component="span">
        {value}
      </Typography>
    </Box>
  );

  return (
    <>
      <MUIDataGrid
        rows={rows}
        rowCount={rowCountState}
        columns={columns}
        getRowId={row => row.id}
        title={t("dashboard.tables.activity_logs.title")}
        loading={isLoading || isFetching}
        onCellClick={({ field, row }) => {
          if (field !== "actions") {
            navigate(`/patients/${row.patient_id}/activity_log`);
          }
        }}
        paginationMode="server"
        paginationModel={{ page: pagination.page, pageSize: pagination.limit }}
        onPaginationModelChange={model => setPagination({ limit: model.pageSize, page: model.page })}
        sortingMode="server"
        sortModel={sort}
        onSortModelChange={setSort}
        disableColumnMenu
        slots={{
          noRowsOverlay: () => (
            <EmptyResultOverlay text={t("dashboard.tables.activity_logs.no_logs")} icon={faNotebook} />
          ),
          noResultsOverlay: () => <EmptyResultOverlay />,
        }}
        localeText={{
          noRowsLabel: "",
          noResultsOverlayLabel: "",
        }}
        notifications={metadata?.total_count}
      />

      {showDialog.show && showDialog.id && showDialog.status && selectedLog && (
        <ActivityLogConfirmDialog
          isOpen
          status={showDialog.status}
          onConfirm={updateActivityLog}
          onCancel={() => {
            setShowDialog(initialDialogState);
            setError(null);
          }}
          isLoading={isUpdating}
          error={error}
        >
          <Stack
            spacing={1}
            sx={{
              backgroundColor: DARK_BLUE[1],
              borderRadius: 2,
              padding: 2,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {renderRow(
              t("dashboard.tables.notes.labels.patient"),
              `${selectedLog.patient_first_name} ${selectedLog.patient_last_name}`
            )}
            {renderRow(t("patients.activity_log.table.activity"), selectedLog.category.description)}
            {renderRow(
              t("patients.activity_log.table.date"),
              isValid(parseISO(selectedLog.start_time)) ? format(parseISO(selectedLog.start_time), "P") : ""
            )}
            {renderRow(
              t("patients.activity_log.table.time"),
              t("patients.activity_log.table.duration", { duration: selectedLog.duration / 60 })
            )}
          </Stack>
        </ActivityLogConfirmDialog>
      )}
    </>
  );
});
